import {
  cancelComicEpisodeGenerateJob,
  normalizeComicEpisodeGenerateJob,
  type ComicEpisodeGenerateJob,
  createComicEpisodeGenerateJob,
  type EpisodeJobPageStatus,
  getComicEpisodeGenerateJob,
  resumeComicEpisodeGenerateJob,
} from '../api/comicEpisodeJobs'
import { getComicProject } from '../api/comics'
import type { ClientUiPublic } from '../api/platformConfig'
import {
  createEpisodeGenerateQueue,
  type EpisodeGenerateQueueState,
  type EpisodeQueuePageStatus,
} from './comicEpisodeGenerateQueue'
import { awaitHubPush, awaitPendingHubTask, SSE_LEGACY_POLL_MS } from './asyncTaskHub'
import { comicEpisodeJobHub } from './comicEpisodeJobEventStream'

const TERMINAL_JOB = new Set(['succeeded', 'failed', 'cancelled'])

function mapPageStatus(status: EpisodeJobPageStatus): EpisodeQueuePageStatus {
  switch (status) {
    case 'running':
      return 'running'
    case 'awaiting_selection':
      return 'awaiting_selection'
    case 'done':
      return 'done'
    case 'failed':
      return 'failed'
    case 'cancelled':
      return 'cancelled'
    default:
      return 'pending'
  }
}

export function syncQueueFromJob(
  base: EpisodeGenerateQueueState,
  job: ComicEpisodeGenerateJob,
): EpisodeGenerateQueueState {
  const statuses = { ...base.statuses }
  for (const p of job.pages) {
    statuses[p.pageId] = mapPageStatus(p.status)
  }
  const running = job.pages.find((p) => p.status === 'running')
  const awaiting = job.pages.find((p) => p.status === 'awaiting_selection')
  const currentPageId = awaiting?.pageId ?? running?.pageId ?? null
  return {
    ...base,
    statuses,
    currentPageId,
    cancelled: job.cancelled,
  }
}

export function buildQueueFromJob(
  job: ComicEpisodeGenerateJob,
  episodeTitle: string,
): EpisodeGenerateQueueState {
  const pageIds = job.pages.map((p) => p.pageId)
  const pageTitles = Object.fromEntries(
    job.pages.map((p) => [p.pageId, (p.title || p.pageId).trim() || p.pageId]),
  )
  return syncQueueFromJob(
    createEpisodeGenerateQueue(job.episodeId, episodeTitle, pageIds, pageTitles),
    job,
  )
}

export type RunEpisodeGenerateJobParams = {
  projectId: string
  episodeId: string
  episodeTitle: string
  pageIds: string[]
  pageTitles: Record<string, string>
  clientUi: ClientUiPublic
  isCancelled: () => boolean
  onQueue: (queue: EpisodeGenerateQueueState) => void
  onProject: (project: Awaited<ReturnType<typeof getComicProject>>) => void
  onJobStarted?: (jobId: string) => void
  waitForPageSelection: (pageId: string) => Promise<void>
}

type PollEpisodeGenerateJobParams = Omit<
  RunEpisodeGenerateJobParams,
  'pageIds' | 'pageTitles'
> & {
  job: ComicEpisodeGenerateJob
  queue: EpisodeGenerateQueueState
}

async function applyJobSnapshot(
  params: PollEpisodeGenerateJobParams,
  job: ComicEpisodeGenerateJob,
  queue: EpisodeGenerateQueueState,
): Promise<{ job: ComicEpisodeGenerateJob; queue: EpisodeGenerateQueueState }> {
  const fresh = await getComicProject(params.projectId, params.clientUi)
  params.onProject(fresh)
  const nextQueue = syncQueueFromJob(queue, job)
  params.onQueue(nextQueue)
  return { job, queue: nextQueue }
}

async function pollEpisodeGenerateJobLoop(
  params: PollEpisodeGenerateJobParams,
): Promise<ComicEpisodeGenerateJob> {
  let job = params.job
  let queue = params.queue
  let pendingSseJob: ComicEpisodeGenerateJob | null = null

  const stopSse = comicEpisodeJobHub.subscribe(job.publicId, (incoming) => {
    pendingSseJob = normalizeComicEpisodeGenerateJob(incoming)
  })

  try {
    while (!TERMINAL_JOB.has(job.status) && !params.isCancelled()) {
      const pendingOpts = { isCancelled: params.isCancelled }
      const pushed = comicEpisodeJobHub.isSseLive()
        ? await awaitHubPush(
            () => pendingSseJob,
            () => {
              pendingSseJob = null
            },
            pendingOpts,
          )
        : await awaitPendingHubTask(
            () => pendingSseJob,
            () => {
              pendingSseJob = null
            },
            Date.now() + SSE_LEGACY_POLL_MS,
            pendingOpts,
          )

      if (params.isCancelled()) {
        await cancelComicEpisodeGenerateJob(params.projectId, job.publicId)
        break
      }

      if (pushed) {
        job = pushed
        ;({ job, queue } = await applyJobSnapshot(params, job, queue))
      } else if (!comicEpisodeJobHub.isSseLive()) {
        job = await getComicEpisodeGenerateJob(params.projectId, job.publicId)
        comicEpisodeJobHub.dispatch(job)
        ;({ job, queue } = await applyJobSnapshot(params, job, queue))
      }

      if (job.status === 'awaiting_selection') {
        const awaiting = job.pages.find((p) => p.status === 'awaiting_selection')
        if (awaiting) {
          await params.waitForPageSelection(awaiting.pageId)
          job = await resumeComicEpisodeGenerateJob(params.projectId, job.publicId)
          comicEpisodeJobHub.dispatch(job)
          ;({ job, queue } = await applyJobSnapshot(params, job, queue))
        }
      }
    }
  } finally {
    stopSse()
  }

  if (params.isCancelled() && !job.cancelled) {
    job = await cancelComicEpisodeGenerateJob(params.projectId, job.publicId)
    comicEpisodeJobHub.dispatch(job)
  }
  if (!TERMINAL_JOB.has(job.status)) {
    job = await getComicEpisodeGenerateJob(params.projectId, job.publicId)
    comicEpisodeJobHub.dispatch(job)
  }
  queue = syncQueueFromJob(queue, job)
  params.onQueue(queue)
  return job
}

export type AttachEpisodeGenerateJobParams = RunEpisodeGenerateJobParams & {
  jobId: string
  initialJob?: ComicEpisodeGenerateJob
}

/** 附着到服务端已有任务（刷新/换设备后不重复创建 job） */
export async function attachEpisodeGenerateJob(
  params: AttachEpisodeGenerateJobParams,
): Promise<ComicEpisodeGenerateJob> {
  const job =
    params.initialJob ?? (await getComicEpisodeGenerateJob(params.projectId, params.jobId))
  comicEpisodeJobHub.dispatch(job)
  params.onJobStarted?.(job.publicId)
  const queue = buildQueueFromJob(job, params.episodeTitle)
  params.onQueue(queue)
  return pollEpisodeGenerateJobLoop({ ...params, job, queue })
}

export async function runEpisodeGenerateJob(
  params: RunEpisodeGenerateJobParams,
): Promise<ComicEpisodeGenerateJob> {
  const job = await createComicEpisodeGenerateJob(params.projectId, params.episodeId, {
    pageIds: params.pageIds,
  })
  comicEpisodeJobHub.dispatch(job)
  params.onJobStarted?.(job.publicId)
  const queue = buildQueueFromJob(job, params.episodeTitle)
  params.onQueue(queue)
  return pollEpisodeGenerateJobLoop({ ...params, job, queue })
}
